import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../store";

export interface HospitalRef {
	id: number;
	name: string;
	[key: string]: any;
}

export interface SurgeonBillingRule {
	id: number | string;
	user_id?: number;
	surgeon_id: number;
	hospital_id: number | null;

	start_date: string | null;
	end_date: string | null;

	default_oop: string | null;
	ifc_required: boolean;

	hospital?: HospitalRef | null;

	created_at?: string;
	updated_at?: string;
}

export interface SurgeonBillingRulesState {
	rulesBySurgeonId: Record<number, SurgeonBillingRule[]>;
	loadingBySurgeonId: Record<number, boolean>;
	selectedSurgeonId: number | null;
	error: string | null;
}

const initialState: SurgeonBillingRulesState = {
	rulesBySurgeonId: {},
	loadingBySurgeonId: {},
	selectedSurgeonId: null,
	error: null,
};

export const surgeonBillingRulesSlice = createSlice({
	name: "surgeonBillingRules",
	initialState,
	reducers: {
		setSelectedSurgeonId: (state, action: PayloadAction<number | null>) => {
			console.debug("[SURGEON BILLING RULES] setSelectedSurgeonId", {
				surgeonId: action.payload,
			});

			state.selectedSurgeonId = action.payload;
		},

		setSurgeonBillingRulesLoading: (
			state,
			action: PayloadAction<{
				surgeonId: number;
				loading: boolean;
			}>,
		) => {
			const { surgeonId, loading } = action.payload;

			console.debug("[SURGEON BILLING RULES] setLoading", {
				surgeonId,
				loading,
			});

			state.loadingBySurgeonId[surgeonId] = loading;
		},

		setSurgeonBillingRulesError: (
			state,
			action: PayloadAction<string | null>,
		) => {
			console.debug("[SURGEON BILLING RULES] setError", {
				error: action.payload,
			});

			state.error = action.payload;
		},

		setSurgeonBillingRulesForSurgeon: (
			state,
			action: PayloadAction<{
				surgeonId: number;
				rules: SurgeonBillingRule[];
			}>,
		) => {
			const { surgeonId, rules } = action.payload;

			console.debug("[SURGEON BILLING RULES] set rules for surgeon", {
				surgeonId,
				count: rules.length,
			});

			state.rulesBySurgeonId[surgeonId] = rules;
		},

		addSurgeonBillingRule: (
			state,
			action: PayloadAction<SurgeonBillingRule>,
		) => {
			const rule = action.payload;
			const surgeonId = rule.surgeon_id;

			console.debug("[SURGEON BILLING RULES] add rule", {
				surgeonId,
				rule,
			});

			if (!state.rulesBySurgeonId[surgeonId]) {
				state.rulesBySurgeonId[surgeonId] = [];
			}

			state.rulesBySurgeonId[surgeonId].push(rule);
		},

		updateSurgeonBillingRule: (
			state,
			action: PayloadAction<{
				surgeonId: number;
				ruleId: number | string;
				updates: Partial<SurgeonBillingRule>;
			}>,
		) => {
			const { surgeonId, ruleId, updates } = action.payload;

			console.debug("[SURGEON BILLING RULES] update rule", {
				surgeonId,
				ruleId,
				updates,
			});

			const rules = state.rulesBySurgeonId[surgeonId] || [];
			const rule = rules.find((item) => item.id === ruleId);

			if (rule) {
				Object.assign(rule, updates);
			}
		},

		removeSurgeonBillingRule: (
			state,
			action: PayloadAction<{
				surgeonId: number;
				ruleId: number | string;
			}>,
		) => {
			const { surgeonId, ruleId } = action.payload;

			console.debug("[SURGEON BILLING RULES] remove rule", {
				surgeonId,
				ruleId,
			});

			state.rulesBySurgeonId[surgeonId] = (
				state.rulesBySurgeonId[surgeonId] || []
			).filter((rule) => rule.id !== ruleId);
		},

		clearSurgeonBillingRulesForSurgeon: (
			state,
			action: PayloadAction<number>,
		) => {
			const surgeonId = action.payload;

			console.debug("[SURGEON BILLING RULES] clear rules for surgeon", {
				surgeonId,
			});

			delete state.rulesBySurgeonId[surgeonId];
			delete state.loadingBySurgeonId[surgeonId];
		},
	},
});

export const {
	setSelectedSurgeonId,
	setSurgeonBillingRulesLoading,
	setSurgeonBillingRulesError,
	setSurgeonBillingRulesForSurgeon,
	addSurgeonBillingRule,
	updateSurgeonBillingRule,
	removeSurgeonBillingRule,
	clearSurgeonBillingRulesForSurgeon,
} = surgeonBillingRulesSlice.actions;

export const selectSurgeonBillingRulesState = (state: RootState) =>
	state.surgeonBillingRules;

export const selectSelectedSurgeonBillingRulesSurgeonId = (state: RootState) =>
	state.surgeonBillingRules.selectedSurgeonId;

export const selectSurgeonBillingRulesForSurgeon =
	(surgeonId: number | null) => (state: RootState) => {
		if (!surgeonId) return [];
		return state.surgeonBillingRules.rulesBySurgeonId[surgeonId] || [];
	};

export const selectSurgeonBillingRulesLoading =
	(surgeonId: number | null) => (state: RootState) => {
		if (!surgeonId) return false;
		return !!state.surgeonBillingRules.loadingBySurgeonId[surgeonId];
	};

export const selectSurgeonBillingRulesError = (state: RootState) =>
	state.surgeonBillingRules.error;

export const selectTimelineRulesForSurgeon =
	(surgeonId: number | null) => (state: RootState) => {
		if (!surgeonId) return [];

		return (
			state.surgeonBillingRules.rulesBySurgeonId[surgeonId] || []
		).filter((rule) => !!rule.start_date);
	};

export const selectLocationRulesForSurgeon =
	(surgeonId: number | null) => (state: RootState) => {
		if (!surgeonId) return [];

		return (
			state.surgeonBillingRules.rulesBySurgeonId[surgeonId] || []
		).filter((rule) => !rule.start_date);
	};

export default surgeonBillingRulesSlice.reducer;
